import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Globe, Plus, Trash2, Edit, AlertCircle, Loader2, DatabaseZap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  Checkbox,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  SearchInput,
  useAuth,
  useLocale,
} from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { getDefaultSources } from '../../constants/referenceDefaults';
import { useSourceListQuery } from '../../hooks/useSourceQueries';
import sourceService from '../../services/source.service';
import { SourceFormDialog } from '../../components/SourceFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { Source } from '../../types/source';

export function SourceListPage() {
  const { user, idToken, currentWorkspace } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');
  const { locale } = useLocale();
  const defaultSources = useMemo(() => getDefaultSources(locale), [locale]);
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search'] });
  const searchValue = filters.search as string | undefined;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingSource, setEditingSource] = useState<Source | null>(null);
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  const [selectedDefaults, setSelectedDefaults] = useState<Set<string>>(
    () => new Set(defaultSources),
  );

  // When the user switches the UI locale while the dialog is closed, reset
  // the selection to the new locale's full set so reopening the dialog
  // reflects what the user now sees.
  useEffect(() => {
    setSelectedDefaults(new Set(defaultSources));
  }, [defaultSources]);

  // React Query for Source list
  const {
    data: sourceResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useSourceListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const sources = sourceResponse?.data?.items || [];
  const totalItems = sourceResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingSource(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: Source) => {
    setFormMode('edit');
    setEditingSource(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingSource(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteClick = useCallback((record: Source) => {
    setSelectedSource(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedSource || !user) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedSource.id));

    try {
      const token = await user.getIdToken();
      await sourceService.deleteSource(currentWorkspace!.id, selectedSource.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedSource(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedSource) {
          newSet.delete(selectedSource.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedSource(null);
      setDeleteError(null);
    }
  };

  const handleCreateDefaults = async () => {
    if (!user || !currentWorkspace || selectedDefaults.size === 0) return;
    setIsCreatingDefaults(true);
    try {
      const token = await user.getIdToken();
      const names = defaultSources.filter((r) => selectedDefaults.has(r));
      await sourceService.bulkCreateSources(currentWorkspace.id, token, names);
      await refetch();
      setShowDefaultDialog(false);
    } catch {
      // surfaced via refetch — keep parity with previous silent handler
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  // Define table columns
  const columns: DataTableColumn<Source>[] = [
    {
      key: 'name',
      title: t('source.columnName'),
      sortable: true,
      render: (value: string) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (only for leader+)
  const actions: DataTableAction<Source>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('config.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('config.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  if (isLoading && !sourceResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="h-8 w-8 text-blue-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('source.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('source.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedDefaults(new Set(defaultSources));
                setShowDefaultDialog(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DatabaseZap className="h-4 w-4" />
              {t('config.createDefaults')}
            </button>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('source.newOne')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <SearchInput
        placeholder={t('source.searchPlaceholder')}
        onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
        initialValue={searchValue || ''}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('source.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={sources}
          loading={isLoading && !sourceResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('source.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('source.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('source.deleteBody')}</AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Source Dialog */}
      <SourceFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        source={editingSource}
        onSuccess={handleFormSuccess}
      />

      {/* Create Defaults Dialog */}
      <AlertDialog open={showDefaultDialog} onOpenChange={(open) => !isCreatingDefaults && setShowDefaultDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('source.defaultsTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{t('config.createDefaultsSelect')}</p>
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={selectedDefaults.size === defaultSources.length}
                      onCheckedChange={(checked) => {
                        setSelectedDefaults(checked ? new Set(defaultSources) : new Set());
                      }}
                    />
                    <span className="text-sm font-medium">{t('config.createDefaultsSelectAll')}</span>
                  </label>
                  <hr className="my-1" />
                  {defaultSources.map((item) => (
                    <label key={item} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedDefaults.has(item)}
                        onCheckedChange={(checked) => {
                          setSelectedDefaults((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(item);
                            else next.delete(item);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-sm">{t('config.createDefaultsSkipNote')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingDefaults}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleCreateDefaults();
              }}
              disabled={isCreatingDefaults || selectedDefaults.size === 0}
            >
              {isCreatingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : (
                t('config.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
